import { useEffect, useRef, useState } from 'react'

import IconButton from '@components/IconButton/IconButton'
import DialogTitle from '@components/mui/DialogTitle'
import { faTrash } from '@fortawesome/pro-solid-svg-icons'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import { DialogProps } from '@toolpad/core/useDialogs'
import { v4 as uuid } from 'uuid'

import {
	checkDuplicateSources,
	cleanStringMaps,
	StringMap,
	StringMapError,
} from '../../FileLayoutEditor.utils'

export default function StringMappingDialog({
	payload,
	open,
	onClose,
}: DialogProps<StringMap[], StringMap[] | null>) {
	const [stringMaps, setStringMaps] = useState<StringMap[]>(payload)
	const [error, setError] = useState<StringMapError | null>(null)

	const previousLength = useRef(stringMaps.length)

	useEffect(() => {
		if (error) {
			const fieldWithError: HTMLElement = document.getElementById(error.id)
			if (fieldWithError) {
				fieldWithError.scrollIntoView()
			}
		}
	}, [error])

	// Set focus to field text field on initial open
	useEffect(() => {
		if (open) {
			setTimeout(() => {
				const inputElementToFocus: HTMLInputElement = document.querySelector('#stringMap-0')
				inputElementToFocus?.focus()
				// Places text cursor at end of text in field
				inputElementToFocus.setSelectionRange(
					inputElementToFocus.value.length,
					inputElementToFocus.value.length
				)
			}, 0)
		}
	}, [open])

	// Set focus to first text field of new string map when added and scroll to it
	useEffect(() => {
		if (stringMaps.length !== previousLength.current) {
			const inputElementToFocus: HTMLInputElement = document.querySelector(
				`#stringMap-${stringMaps.length - 1}`
			)
			inputElementToFocus?.focus()
			inputElementToFocus?.scrollIntoView()
			previousLength.current = stringMaps.length
		}
	}, [stringMaps])

	const handleSaveStringMaps = () => {
		const cleanedStringMaps = cleanStringMaps(stringMaps)
		const stringMapError = checkDuplicateSources(cleanedStringMaps)
		if (stringMapError) {
			setError(stringMapError)
			return
		}
		return onClose(cleanedStringMaps)
	}

	const handleAddStringMap = () => {
		setStringMaps((prev) => [...prev, { id: uuid(), target: '', sources: '' }])
	}

	const handleRemoveStringMap = (idToRemove: string) => {
		const updatedStringMaps = stringMaps.filter((stringMap) => stringMap.id !== idToRemove)
		if (!updatedStringMaps.length) {
			return setStringMaps([{ id: uuid(), target: '', sources: '' }])
		}
		setStringMaps(updatedStringMaps)
	}

	const handleEditStringMaps = (value: string, field: string, id: string) => {
		const updatedStringMaps = stringMaps.map((stringMap) =>
			stringMap.id === id ? { ...stringMap, [field]: value } : stringMap
		)
		setStringMaps(updatedStringMaps)
	}

	return (
		<Dialog
			fullWidth
			maxWidth="sm"
			open={open}
			onClose={() => onClose(null)}
			closeAfterTransition={false}
		>
			<DialogTitle onClose={() => onClose(null)}>String Mapping Editor</DialogTitle>
			<DialogContent dividers sx={{ height: 600 }}>
				<Stack gap={2}>
					<Box>
						<Stack direction="row" alignItems="center" p={1} gap={4}>
							<Typography sx={{ width: '100%' }} variant="body2">
								<b>Find</b>
							</Typography>
							<Typography textAlign="left" sx={{ width: '100%' }} variant="body2">
								<b>REPLACE WITH</b>
							</Typography>
						</Stack>
						<Divider />
					</Box>
					{stringMaps.map((stringMap, i) => (
						<Box key={stringMap.id} id={stringMap.id}>
							<Stack direction="row" gap={4} mb={2}>
								<TextField
									id={`stringMap-${i}`}
									focused={error?.id === stringMap.id}
									error={error?.id === stringMap.id}
									helperText={
										error?.id === stringMap.id
											? `String "${error.string}" already mapped`
											: 'One per line'
									}
									sx={{ width: '100%' }}
									value={stringMap.sources}
									rows={3}
									multiline
									onChange={(e) => handleEditStringMaps(e.target.value, 'sources', stringMap.id)}
								/>
								<Stack direction="row" gap={4} sx={{ width: '100%' }}>
									<TextField
										autoComplete="off"
										sx={{ width: '100%' }}
										value={stringMap.target}
										onChange={(e) => handleEditStringMaps(e.target.value, 'target', stringMap.id)}
									/>
									<IconButton
										sx={{ height: '2rem', alignSelf: 'center' }}
										onClick={() => handleRemoveStringMap(stringMap.id)}
										color="error"
										size="small"
										icon={faTrash}
									/>
								</Stack>
							</Stack>
							<Divider />
						</Box>
					))}
					<Button
						disabled={
							!stringMaps[stringMaps.length - 1].target ||
							!stringMaps[stringMaps.length - 1].sources
						}
						variant="contained"
						color="secondary"
						sx={{ width: 200, margin: 'auto' }}
						onClick={handleAddStringMap}
					>
						Add String Map
					</Button>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={() => onClose(null)}>
					Cancel
				</Button>
				<Button variant="contained" onClick={() => handleSaveStringMaps()}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	)
}
